<template>
  <div
    v-show="isVisible"
    :id="'comment-' + comment.id"
    class="war-room-comment"
    :class="{ 'war-room-comment--nested': nestingLevel > 1 }"
    data-test="war_room_comment"
  >
    <div class="war-room-comment__left-bar">
      <WarRoomVote :comment="comment" :post="null" />
      <div class="war-room-comment__thread-separator" />
    </div>
    <div class="war-room-comment__right-bar">
      <transition name="fade">
        <div
          v-if="isHighlightedBackgroundVisible"
          class="war-room-comment__right-bar-bg-highlight"
          data-test="comment-highlight-block"
        />
      </transition>
      <div class="war-room-comment__right-bar-inner" data-test="comment-block">
        <template v-if="isEditMode">
          <WarRoomCommentCreateFormEditor
            class="war-room-comment__editor"
            :initial-text="fixComment(comment.text)"
            :is-anonymous="comment.is_anonymous"
            :image-key="editedImageKey"
            mode="edit"
            autofocus
            @text-change="editCommentText = $event"
            @post-shortcut-entered="editComment"
            @image-remove="editImageRemove"
            @image-change="editImageChange"
            @image-uploading="editImageUploading"
          >
            <template #toolbar>
              <div class="war-room-comment__edit-actions">
                <div>
                  <BravadoButtonV3
                    :disabled="isSaveEditDisabled"
                    variant="accent"
                    size="sm"
                    data-test="save-after-edit-button"
                    @click="editComment"
                  >
                    Save
                  </BravadoButtonV3>
                </div>
                <div>
                  <BravadoButtonV3 variant="secondary" size="sm" @click="isEditMode = false">Cancel</BravadoButtonV3>
                </div>
                <div class="war-room-comment__edit-remove">
                  <BravadoButtonV3
                    variant="danger"
                    size="sm"
                    data-test="remove-after-edit-button"
                    @click="removeCommentClick"
                  >
                    Remove
                  </BravadoButtonV3>
                </div>
              </div>
            </template>
          </WarRoomCommentCreateFormEditor>
        </template>
        <div v-if="!isEditMode">
          <p v-if="comment.is_deleted" class="war-room-comment__deleted-note">This comment was removed</p>
          <div v-else data-test="content-block">
            <header class="war-room-comment__header">
              <WarRoomCommentAuthor :author="author" class="war-room-comment__author" />

              <Dropdown position="right">
                <DropdownToggler data-test="actions-toggle" class="war-room-comment__actions-toogler">
                  <MoreIcon class="war-room-comment__actions-icon" />
                </DropdownToggler>

                <DropdownMenu slot="menu">
                  <DropdownItem
                    v-for="(action, actionIndex) in actions"
                    :key="actionIndex"
                    data-test="war-room__comment-action"
                    @click="action.action"
                  >
                    <div class="war-room-comment__icon-wrapper">
                      <Component :is="action.icon" class="war-room-comment__dropdown-icon" />
                    </div>
                    {{ action.name }}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </header>
            <div class="war-room-comment__content">
              <!-- eslint-disable vue/no-v-html -->
              <span
                class="war-room-comment__text"
                data-test="war_room_comment_text"
                v-html="fixComment(comment.text)"
              />
              <div v-if="imageUrl" class="war-room-comment__image-wrapper" data-test="war_room_comment_text">
                <NuxtImg
                  :key="imageUrl"
                  alt=""
                  data-test="comment-image"
                  class="war-room-comment__image"
                  preset="comment"
                  :src="imageUrl"
                  width="auto"
                  height="auto"
                  loading="lazy"
                  @click="toggleExpandedImage"
                />
              </div>

              <Portal v-if="imageOpenend" to="comment-image">
                <div class="war-room-comment__full-image-wrapper" @click="hideExpandedImage">
                  <NuxtImg
                    :key="imageUrl"
                    alt=""
                    class="war-room-comment__full-image"
                    preset="expanded-comment"
                    :src="imageUrl"
                    :modifiers="expandedImgModifiers"
                    width="auto"
                    height="auto"
                    loading="lazy"
                  />
                </div>
              </Portal>
            </div>
            <div class="war-room-comment__actions">
              <div class="war-room-comment__actions-buttons">
                <button
                  class="war-room-comment__button war-room-comment__action"
                  data-test="open-reply-button"
                  @click="toggleCreateCommentInput"
                >
                  <ReplyIcon class="war-room-comment__icon" />
                  Reply
                </button>
                <button
                  v-if="isAuthor && !$device.isMobile"
                  class="war-room-comment__button war-room-comment__action"
                  @click="enterEditMode"
                >
                  <EditIcon class="war-room-comment__icon" />
                  Edit
                </button>
                <WarRoomShare
                  :sharable-type="sharableType"
                  :sharable-url="sharableUrl"
                  :sharable-id="comment.id"
                  class="war-room-comment__action"
                />
              </div>
              <div class="war-room-comment__right-actions">
                <div v-if="isUnreadIconVisible" class="war-room-comment__unread-icon" />
                <div class="war-room-comment__date" data-test="create-time-block">
                  {{ commentDate }}{{ comment.is_updated ? ' (Edited)' : '' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <WarRoomCommentCreateForm
          v-if="isCreateCommentInputVisible"
          ref="commentCreateForm"
          :autofocus="isCreateCommentInputVisible"
          :nested="true"
          :boost-usernames="boostUsernames"
          :upserting="upserting"
          @post-comment="onPostComment"
          @cancel="toggleCreateCommentInput"
        />
        <WarRoomComments
          :comments="comment.replies"
          :nesting-level="nestingLevel + 1"
          :parent-id="comment.id"
          :post="post"
          :boost-usernames-nested="boostUsernames"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import type { Component } from 'vue';
import type { RawLocation } from 'vue-router';
import { mapActions, mapMutations, mapState } from 'vuex';
import { format, isToday, isYesterday } from 'date-fns';
import { getActiveStorageUrl } from '../WarRoomTextEditor/utils';
import EditIcon from './images/edit.vue.svg';
import ReplyIcon from './images/reply.vue.svg';
import WarRoomCommentCreateForm from '@/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateForm.vue';
import WarRoomCommentAuthor from '@/components/partials/war-room/WarRoomComment/WarRoomCommentAuthor.vue';
import MoreIcon from '@/assets/svg/more.vue.svg';
import WarRoomVote from '@/components/partials/war-room/WarRoomVote/WarRoomVote.vue';
import WarRoomShare from '@/components/partials/war-room/WarRoomShare/WarRoomShare.vue';
import { sharableTypes } from '@/components/partials/war-room/WarRoomShare/constants';
import { makeUrl } from '@/components/utils/browser/url';
import FlagIcon from '@/assets/svg/u_flag.vue.svg';
import TrashIcon from '@/assets/svg/u_trash-alt.vue.svg';
import Dropdown from '@/components/common/Dropdown/Dropdown.vue';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu.vue';
import DropdownToggler from '@/components/common/Dropdown/DropdownToggler.vue';
import DropdownItem from '@/components/common/Dropdown/DropdownItem.vue';
import { HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE } from '@/constants';
import WarRoomComments from '~/components/partials/war-room/WarRoomComments/WarRoomComments.vue';
import WarRoomCommentCreateFormEditor from '~/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateFormEditor.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import type { IPostComment, IPostUser } from '~/types/community';
import { isExtraRoleAdmin, dropRedundantWhitespace, fixLinkWhitespace } from '~/components/partials/war-room/utils';
import type { Cloudinary } from '~/nuxtconfig/image/types';
import { LAST_NESTING_LEVEL } from '~/components/partials/war-room/constants';

type PostCommentResponse = {
  comment_dict: Record<number, IPostComment>;
  user_dict: Record<number, IPostUser>;
  public_user_dict: Record<number, IPostUser>;
};

interface IData {
  isCreateCommentInputVisible: boolean;
  upserting: boolean;
  isEditMode: boolean;
  editCommentText: string;
  isHighlightedBackgroundVisible: boolean;
  sharableType: string;
  editedImageKey: string | null;
  editedImageMedia: string | null;
  imageUploading: boolean;
  imageOpenend: boolean;
  isVisible: boolean;
}

interface IAction {
  name: string;
  action: Function;
  visible: boolean;
  icon?: Component;
}

const BRAVOS_DOSE = 5;

export default Vue.extend({
  name: 'WarRoomComment',
  components: {
    WarRoomCommentCreateFormEditor,
    WarRoomCommentAuthor,
    ReplyIcon,
    EditIcon,
    WarRoomCommentCreateForm,
    WarRoomVote,
    WarRoomComments,
    BravadoButtonV3,
    WarRoomShare,
    FlagIcon,
    MoreIcon,
    Dropdown,
    DropdownMenu,
    DropdownToggler,
    DropdownItem,
    TrashIcon,
  },
  props: {
    comment: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      required: true,
    },
    post: {
      type: Object,
      default: () => ({}),
    },
    boostUsernames: {
      type: Array,
      default: () => [],
    },
    nestingLevel: {
      type: Number,
      default: 1,
    },
    parentId: {
      type: Number,
      default: null,
    },
  },
  data(): IData {
    return {
      isCreateCommentInputVisible: false,
      upserting: false,
      isEditMode: false,
      editCommentText: '',
      isHighlightedBackgroundVisible: false,
      sharableType: sharableTypes.COMMENT,
      editedImageKey: null,
      editedImageMedia: null,
      imageUploading: false,
      imageOpenend: false,
      isVisible: false,
    };
  },
  computed: {
    ...mapState(['settings']),
    ...mapState('warRoom', ['user_dict', 'public_user_dict']),
    actions(): IAction[] {
      return [
        {
          name: 'Hide',
          action: this.handleHide,
          visible: this.hasExtraActions,
        },
        {
          name: 'Mass Downvote',
          action: this.handleRating,
          visible: this.hasExtraActions,
        },
        {
          name: 'Reply',
          action: this.toggleCreateCommentInput,
          visible: true,
          icon: ReplyIcon,
        },
        {
          name: 'Edit',
          action: this.enterEditMode,
          visible: this.isAuthor,
          icon: EditIcon,
        },
        {
          name: 'Remove',
          action: this.removeCommentClick,
          visible: this.isAuthor || this.hasExtraActions,
          icon: TrashIcon,
        },
        {
          name: 'Flag an issue',
          action: this.openFlagIssueModal,
          visible: !this.isAuthor,
          icon: FlagIcon,
        },
      ].filter(a => a.visible);
    },
    author(): IPostUser {
      const postComment = this.comment as IPostComment;
      return this.user_dict[postComment.user_username] || this.public_user_dict[postComment.user_slug];
    },
    hasExtraActions(): boolean {
      return Boolean(this.$auth.user?.admin) && isExtraRoleAdmin();
    },
    isAuthor(): boolean {
      return (
        this.$auth?.loggedIn &&
        (this.$auth?.user.username === this.comment.user_username || this.$auth?.user.slug === this.comment.user_slug)
      );
    },
    isLastNestingLevel(): boolean {
      return LAST_NESTING_LEVEL === this.nestingLevel;
    },
    commentDate(): string {
      const comment: IPostComment = this.comment;
      const dateFieldToUse = comment.is_updated ? comment.updated_at! : comment.created_at;
      const commentDate = new Date(dateFieldToUse);

      let datePart;
      if (isToday(commentDate)) {
        datePart = 'Today';
      } else if (isYesterday(commentDate)) {
        datePart = 'Yesterday';
      } else {
        datePart = format(commentDate, 'MMM, dd');
      }

      return `${datePart}, ${format(commentDate, 'hh:mm aaa')}`;
    },
    isCommentTextEmpty(): boolean {
      return !this.editCommentText.replace(/&nbsp;|<br>/g, '').trim().length;
    },
    isSaveEditDisabled(): boolean {
      return this.isCommentTextEmpty || this.imageUploading;
    },
    isHighlighted(): boolean {
      return !!this.$route.params.commentId && Number(this.$route.params.commentId) === this.comment.id;
    },
    sharableUrl(): string {
      const route: RawLocation = {
        name: 'war-room-posts-postSlug-comments-commentId',
        params: { postSlug: this.post.slug, commentId: this.comment.id },
      };

      const { href } = this.$router.resolve(route);

      return makeUrl(href);
    },
    isUnreadIconVisible(): boolean {
      if (!this.$auth.loggedIn) return false;
      if (!this.post.viewed) return false;

      return !this.comment.viewed;
    },
    imageUrl(): string | null {
      return this.comment.image_key && getActiveStorageUrl(this.comment.image_key);
    },
    expandedImgModifiers(): Partial<Cloudinary.Modifiers> {
      return { ...(process.client && { h: (window.innerHeight * 0.9).toFixed() }) };
    },
  },
  watch: {
    '$route.params.commentId': {
      handler() {
        this.setUpCommentInersectionObserver();
      },
    },
  },
  beforeCreate() {
    this.$options.components!.WarRoomComments =
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require('@/components/partials/war-room/WarRoomComments/WarRoomComments.vue').default;
  },
  mounted() {
    this.setUpCommentInersectionObserver();

    const { specialUsers } = this.settings;
    const hideAfter = HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE.getTime();

    const createdAt = new Date(this.comment.created_at).getTime();
    const timeDiff = hideAfter - createdAt;

    if (
      specialUsers.includes(this.comment.user_username) &&
      !specialUsers.includes(this.$auth.user?.username) &&
      timeDiff < 0
    ) {
      this.hideCommentMutation({ post: this.post, commentId: this.comment.id });
    } else {
      this.isVisible = true;
    }
  },
  methods: {
    ...mapActions('warRoom', ['updateComment', 'hideComment', 'commentMassDownVote']),
    ...mapMutations('warRoom', ['addComment', 'addUsers']),
    ...mapMutations('warRoom', { hideCommentMutation: 'hideComment' }),
    toggleCreateCommentInput() {
      this.$analytics.logEvent('war-room__comment__reply__click');
      if (this.$auth.loggedIn) {
        this.isCreateCommentInputVisible = !this.isCreateCommentInputVisible;
      } else {
        this.$router.push(`?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`);
      }
    },
    async removeCommentClick() {
      this.$analytics.logEvent('war-room__comment__remove__click');
      await this.$router.replace({
        query: { ...this.$route.query, dynamic_modal: 'confirm-remove-comment', commentId: this.comment.id },
      });
    },
    // create reply
    async onPostComment({ text, media, is_anonymous }: { text: string; media: string; is_anonymous: boolean }) {
      this.upserting = true;
      const parentId = this.isLastNestingLevel ? this.parentId : this.comment.id;
      const insertAt = this.isLastNestingLevel ? this.index + 1 : 0;

      try {
        const {
          comment_dict: commentDict,
          user_dict: userDict,
          public_user_dict: publicUserDict,
        } = await this.$api.$post<PostCommentResponse>('/api/war_room/comments.json', {
          comment: {
            text,
            post_id: this.post.id,
            parent_id: parentId,
            is_anonymous,
            media,
          },
        });

        const createdComment = Object.values(commentDict)[0];

        this.$analytics.logEvent('war-room__1-post__comment__create', {
          comment_id: createdComment?.id,
          replyTo: 'comment',
        });
        // eslint-disable-next-line
        (this.$refs.commentCreateForm as any).clearEditor();
        this.isCreateCommentInputVisible = false;

        this.addUsers({ userDict, publicUserDict });
        Object.values(commentDict).forEach(comment => {
          this.addComment({ post: this.post, comment, parentId, insertAt });
        });
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      } finally {
        this.upserting = false;
      }
    },
    edit() {
      // eslint-disable-next-line
      (this.$refs.commentCreateForm as any).setText(this.comment.text);
      this.toggleCreateCommentInput();
    },
    async editComment() {
      if (this.imageUploading) {
        return;
      }

      try {
        // eslint-disable-next-line
        const newCommentField: any = {
          commentId: this.comment.id,
          text: dropRedundantWhitespace(this.editCommentText),
        };

        // if was uploaded new image we should check media
        if (this.editedImageMedia) {
          newCommentField.media = this.editedImageMedia;
          // if was deleted old image we should check key
        } else if (!this.editedImageKey) {
          newCommentField.media = null;
        }

        await this.updateComment(newCommentField);
      } catch (e) {
        this.$notice.error('Could not update comment.', e);
        this.$sentryUtils.error(e);
      }

      this.isEditMode = false;
      this.editCommentText = '';
    },
    enterEditMode() {
      this.$analytics.logEvent('war-room__comment__edit__click');

      this.editImageChange({ key: this.comment.image_key, media: null });

      this.isEditMode = true;
    },
    editImageRemove() {
      this.editImageChange({ key: null, media: null });
    },
    editImageChange({ key, media }: { key: string | null; media: string | null }) {
      this.editedImageMedia = media;
      this.editedImageKey = key;
    },
    editImageUploading(state: boolean) {
      this.imageUploading = state;
    },
    setUpCommentInersectionObserver() {
      if (!this.isHighlighted) return;

      this.isHighlightedBackgroundVisible = true;

      if ('IntersectionObserver' in window) {
        const intersectionObserver = new IntersectionObserver(entries => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            setTimeout(() => {
              this.isHighlightedBackgroundVisible = false;
              intersectionObserver.disconnect();
            }, 2000);
          }
        });
        intersectionObserver.observe(this.$el);
      } else {
        setTimeout(() => {
          this.isHighlightedBackgroundVisible = false;
        }, 2000);
      }
    },
    openFlagIssueModal(): void {
      this.$modal.showWithRoute(
        { custom: 'flag-issue' },
        { flaggableId: String(this.comment.id), flaggableType: 'WarRoom::Comment' },
      );
    },
    async handleHide() {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          dynamic_modal: 'confirm-hide-comment',
          commentId: this.comment.id,
          text: encodeURI(this.comment.text),
        },
      });
    },
    handleRating(): void {
      this.commentMassDownVote({ comment: this.comment, attributes: { downvotes_count: BRAVOS_DOSE } });
    },
    toggleExpandedImage() {
      if (!this.imageOpenend) {
        window.document.body.setAttribute('locked', 'true');
        this.imageOpenend = true;
      } else {
        this.hideExpandedImage();
      }
    },
    hideExpandedImage() {
      window.document.body.removeAttribute('locked');
      this.imageOpenend = false;
    },

    fixComment(text: string) {
      return fixLinkWhitespace(text);
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-comment {
  display: grid;
  grid-template-columns: 14px 1fr;
  grid-column-gap: 0;
  margin-top: 32px;
  position: relative;
  max-width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  @include mq('sm') {
    grid-column-gap: 16px;
  }

  &--nested {
    margin: 16px 0 0 -14px;

    @include mq('sm') {
      margin: 16px 0 0 0;
    }
  }

  &__left-bar {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__thread-separator {
    width: 2px;
    flex-grow: 1;
    border-radius: 8px;
    background: var(--theme-color-over-card);
    margin-top: 6px;
  }

  &__author {
    flex-grow: 1;
    margin-right: 10px;
  }

  &__text {
    @include body;
    @include break-word;

    color: var(--theme-color-text);
    margin-bottom: 4px;

    ::v-deep a {
      color: var(--blue-50);
      text-decoration: underline;
    }
  }

  &__button {
    @include flex-center;
    @include subhead;

    font-weight: 600;
    outline: none;
    appearance: none;
    border: none;
    color: var(--theme-color-sub-text);
    height: 32px;
    background: transparent;
    cursor: pointer;
    padding: 0;

    @include color-hover(sub_text);
  }

  &__action {
    margin-right: 16px;
  }

  &__icon-wrapper {
    width: 32px;
    height: 32px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 4px;
    border-radius: 6px;
    margin-right: 8px;
    background-color: var(--theme-color-background);
    vertical-align: middle;
  }

  &__dropdown-icon {
    color: var(--theme-color-sub-text);
    width: 24px;
    height: 24px;
  }

  &__icon {
    color: inherit;
    margin-right: 6px;
    width: 16px;
    height: 16px;
  }

  &__actions {
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-column-gap: 16px;
    align-items: center;
    margin-top: 4px;
  }

  &__actions-toogler {
    line-height: 0;
  }

  &__actions-icon {
    width: 16px;
    height: 16px;
    color: var(--gray-50);
  }

  //&__comment-actions {}

  &__actions-buttons {
    display: flex;
    align-items: center;
  }

  &__edit-actions {
    display: grid;
    grid-template-columns: auto auto 1fr;
    grid-column-gap: 16px;
  }

  &__edit-remove {
    justify-self: flex-end;
  }

  &__editor {
    min-height: 88px !important;
    margin-bottom: 16px;
  }

  &__deleted-note {
    @include subhead;

    color: var(--gray-50);
    padding: 14px 16px;
    background: var(--theme-color-over-card);
    border-radius: 6px;
  }

  &__right-actions {
    display: flex;
    justify-self: flex-end;
    align-items: center;
  }

  &__unread-icon {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--blue-60);
    display: inline-block;
    margin-right: 6px;
    flex-shrink: 0;
    margin-top: 5px;
  }

  &__date {
    @include caption2;

    color: var(--gray-50);
    margin-right: 8px;
  }

  &__right-bar {
    position: inherit;
    padding-left: 12px;
    min-width: 0;

    @include mq('sm') {
      padding-left: 0;
    }
  }

  &__right-bar-inner {
    position: relative;
  }

  &__right-bar-bg-highlight {
    background: var(--theme-color-highlight);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(calc(-50%), calc(-50%));
    width: calc(100% + 24px);
    height: calc(100% + 16px);
    border-radius: 6px;
  }

  &__content {
    overflow: hidden;
  }

  &__image-wrapper {
    margin-top: 4px;
    display: flex;
    overflow: hidden;
  }

  &__image {
    border-radius: 8px;
    max-height: 258px;
    max-width: 100%;
    // stylelint-disable-next-line
    aspect-ratio: auto 700 / 258;
    cursor: pointer;
  }

  &__full-image-wrapper {
    background: rgba(0, 0, 0, 0.6);
    position: fixed;
    z-index: 200;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  &__full-image {
    max-height: 90vh;
    max-width: 100%;
    // stylelint-disable-next-line
    aspect-ratio: auto 700 / 258;
    margin-top: 50px;
  }
}
</style>
